import React from "react";
import { normalizeDialable } from "../../data/phoneNumbers";
import { PhoneCall } from "../../services/PhoneCallsService";
import RecordingPlayer from "../molecules/RecordingPlayer";

type PhoneCallsTableProps = {
  calls: PhoneCall[];
  loading: boolean;
  /** Null hides the call-back column, for example when the softphone is offline. */
  // eslint-disable-next-line no-unused-vars
  onCall: ((phoneNumber: string) => void) | null;
};

export function formatDuration(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;

  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}

/** The number on the other end of the call, whichever way it went. */
export function counterparty(call: PhoneCall): string {
  return call.direction === "inbound" ? call.from_number : call.to_number;
}

/** Our side of the call: the line that rang, or the caller ID used to dial. */
export function ownLine(call: PhoneCall): string {
  return call.direction === "inbound" ? call.to_number : call.from_number;
}

const headerClasses =
  "px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider";
const cellClasses = "px-4 py-3 text-sm text-gray-200 whitespace-nowrap";

export default function PhoneCallsTable({
  calls,
  loading,
  onCall,
}: PhoneCallsTableProps) {
  if (loading) {
    return <p className="py-8 text-center text-gray-400">Loading calls…</p>;
  }

  if (calls.length === 0) {
    return <p className="py-8 text-center text-gray-400">No calls yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-700">
        <thead>
          <tr>
            <th className={headerClasses}>When</th>
            <th className={headerClasses}>Direction</th>
            <th className={headerClasses}>Number</th>
            <th className={headerClasses}>Our Number</th>
            <th className={headerClasses}>Customer</th>
            <th className={headerClasses}>Rep</th>
            <th className={headerClasses}>Duration</th>
            <th className={headerClasses}>Status</th>
            <th className={headerClasses}>Recording</th>
            {onCall ? <th className={headerClasses}>Actions</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {calls.map((call) => {
            // Withheld caller IDs ("anonymous") and browser legs ("client:...")
            // cannot be dialed back; offering to only produces a failed call.
            const callBackNumber = onCall
              ? normalizeDialable(counterparty(call))
              : null;

            return (
              <tr key={call.id}>
                <td className={cellClasses}>{call.created_at}</td>
                <td className={cellClasses}>{call.direction}</td>
                <td className={cellClasses}>{counterparty(call)}</td>
                <td className={cellClasses}>{ownLine(call)}</td>
                <td className={cellClasses}>
                  {call.customer_name || "Unknown"}
                </td>
                <td className={cellClasses}>
                  {call.rep_username || "Unassigned"}
                </td>
                <td className={cellClasses}>{formatDuration(call.duration)}</td>
                <td className={cellClasses}>
                  {Number(call.is_voicemail) === 1 ? "voicemail" : call.status}
                </td>
                <td className={cellClasses} aria-label="Recording">
                  <RecordingPlayer
                    callId={call.id}
                    hasRecording={Boolean(call.recording_sid)}
                  />
                </td>
                {onCall ? (
                  <td className={cellClasses}>
                    {callBackNumber ? (
                      <button
                        type="button"
                        onClick={() => onCall(callBackNumber)}
                        className="px-3 py-1 rounded-md bg-emerald-600 text-xs text-white hover:bg-emerald-500"
                      >
                        Call back
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
